/**
 * Query Optimization Service
 * Monitors and optimizes database queries
 */

import { supabase } from './supabase'
import { captureException } from './errorTracking'

/**
 * Query Performance Monitor
 */
class QueryPerformanceMonitor {
  constructor() {
    this.queryCache = new Map()
    this.slowQueries = []
    this.queryStats = {
      total: 0,
      slow: 0,
      cached: 0,
      failed: 0,
      timeoutErrors: 0,
      avgDuration: 0,
      maxDuration: 0,
    }
    this.durationHistory = [] // Track durations for statistics
  }

  /**
   * Execute query with performance monitoring and timeout
   */
  async execute(queryFn, context = {}) {
    const startTime = performance.now()
    const cacheKey = this.getCacheKey(context)

    // Check cache first
    if (this.queryCache.has(cacheKey)) {
      const cached = this.queryCache.get(cacheKey)
      if (Date.now() - cached.timestamp < (context.cacheTTL || 60000)) {
        // 1 minute default TTL
        this.queryStats.cached++
        return cached.data
      }
      this.queryCache.delete(cacheKey)
    }

    try {
      // Apply timeout if specified
      const { withTimeout, getTimeoutForQuery, QUERY_TIMEOUTS } = await import('../utils/queryTimeout')
      const timeoutMs = context.timeout || getTimeoutForQuery(context.operation || 'select', context.estimatedCount || 0)

      // Execute query with timeout
      const queryPromise = queryFn()
      const result = await withTimeout(queryPromise, timeoutMs, context)
      const duration = performance.now() - startTime

      this.queryStats.total++

      // Update duration statistics
      this.durationHistory.push(duration)
      if (this.durationHistory.length > 1000) {
        this.durationHistory.shift() // Keep last 1000
      }

      // Calculate average duration
      const sum = this.durationHistory.reduce((a, b) => a + b, 0)
      this.queryStats.avgDuration = sum / this.durationHistory.length

      // Update max duration
      if (duration > this.queryStats.maxDuration) {
        this.queryStats.maxDuration = duration
      }

      // Track slow queries (>1 second)
      if (duration > 1000) {
        this.queryStats.slow++
        this.slowQueries.push({
          ...context,
          duration,
          timestamp: new Date().toISOString(),
          timeout: timeoutMs,
        })

        // Keep only last 100 slow queries
        if (this.slowQueries.length > 100) {
          this.slowQueries.shift()
        }

        // Log slow query
        console.warn('Slow query detected:', {
          ...context,
          duration: `${duration.toFixed(2)}ms`,
          timeout: `${timeoutMs}ms`,
        })

        // Performance monitoring removed - was causing page reloads
      }

      // Cache result if requested
      if (context.cache !== false) {
        this.queryCache.set(cacheKey, {
          data: result,
          timestamp: Date.now(),
        })
      }

      return result
    } catch (error) {
      this.queryStats.failed++

      // Check if it's a timeout error
      if (error.message && error.message.includes('timeout')) {
        this.queryStats.timeoutErrors = (this.queryStats.timeoutErrors || 0) + 1
        console.error('Query timeout:', {
          ...context,
          timeout: error.timeout,
        })
      }

      captureException(error, {
        context: 'query_execution',
        ...context,
      })
      throw error
    }
  }

  /**
   * Generate cache key
   */
  getCacheKey(context) {
    return JSON.stringify({
      table: context.table,
      filters: context.filters,
      select: context.select,
    })
  }

  /**
   * Clear cache
   */
  clearCache(table = null) {
    if (table) {
      // Clear cache for specific table
      for (const [key, value] of this.queryCache.entries()) {
        const cached = JSON.parse(key)
        if (cached.table === table) {
          this.queryCache.delete(key)
        }
      }
    } else {
      // Clear all cache
      this.queryCache.clear()
    }
  }

  /**
   * Get query statistics
   */
  getStats() {
    return {
      ...this.queryStats,
      cacheSize: this.queryCache.size,
      slowQueries: this.slowQueries.length,
      cacheHitRate:
        this.queryStats.total > 0
          ? (this.queryStats.cached / this.queryStats.total) * 100
          : 0,
    }
  }

  /**
   * Get slow queries
   */
  getSlowQueries(limit = 20) {
    return this.slowQueries
      .sort((a, b) => b.duration - a.duration)
      .slice(0, limit)
  }
}

// Singleton instance
const queryMonitor = new QueryPerformanceMonitor()

/**
 * Optimized query function
 */
export const optimizedQuery = async (queryFn, context = {}) => {
  return queryMonitor.execute(queryFn, context)
}

/**
 * Query with pagination and caching
 */
export const queryWithPagination = async (
  table,
  options = {},
  context = {}
) => {
  const {
    page = 1,
    pageSize = 20,
    filters = {},
    orderBy = { column: 'created_at', ascending: false },
    select = '*',
    cache = true,
    cacheTTL = 60000, // 1 minute
  } = options

  return optimizedQuery(
    async () => {
      const offset = (page - 1) * pageSize

      // Get total count (cached separately)
      const countResult = await optimizedQuery(
        async () => {
          const { count } = await supabase
            .from(table)
            .select('*', { count: 'exact', head: true })
            .match(filters)
          return count
        },
        {
          table: `${table}_count`,
          filters,
          cache,
          cacheTTL,
        }
      )

      // Get paginated data
      let query = supabase.from(table).select(select)

      // Apply filters
      Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          query = query.in(key, value)
        } else if (value !== undefined && value !== null) {
          query = query.eq(key, value)
        }
      })

      // Apply ordering
      query = query.order(orderBy.column, { ascending: orderBy.ascending !== false })

      // Apply pagination (enforce maximum page size)
      const maxPageSize = 1000 // Maximum records per page
      const safePageSize = Math.min(pageSize, maxPageSize)
      query = query.range(offset, offset + safePageSize - 1)

      // Apply timeout
      const { withTimeout, QUERY_TIMEOUTS } = await import('../utils/queryTimeout')
      const queryPromise = query
      const { data, error } = await withTimeout(queryPromise, QUERY_TIMEOUTS.NORMAL, {
        table,
        operation: 'select',
        page,
        pageSize: safePageSize,
      })

      if (error) throw error

      return {
        data: data || [],
        pagination: {
          page,
          pageSize: safePageSize,
          total: countResult || 0,
          totalPages: Math.ceil((countResult || 0) / safePageSize),
          hasNext: page * safePageSize < (countResult || 0),
          hasPrevious: page > 1,
        },
      }
    },
    {
      table,
      filters,
      select,
      page,
      pageSize,
      cache,
      cacheTTL,
      ...context,
    }
  )
}

/**
 * Clear query cache
 */
export const clearQueryCache = (table = null) => {
  queryMonitor.clearCache(table)
}

/**
 * Get query performance statistics
 */
export const getQueryStats = () => {
  return queryMonitor.getStats()
}

/**
 * Get slow queries
 */
export const getSlowQueries = (limit = 20) => {
  return queryMonitor.getSlowQueries(limit)
}

export default {
  optimizedQuery,
  queryWithPagination,
  clearQueryCache,
  getQueryStats,
  getSlowQueries,
}

